/**
 * Configuration for retry behavior in AI request handling
 */
export interface RetryConfig {
	/**
	 * Maximum number of retry attempts
	 * @default 1
	 */
	maxRetries: number;

	/**
	 * Delay in milliseconds between retry attempts
	 * @default 1500
	 */
	retryDelay: number;

	/**
	 * Whether to retry when response validation fails (text-only, empty JSON, etc.)
	 * Uses ValidateAIResponse() to check response validity
	 * @default true
	 */
	shouldRetryOnInvalidResponse: boolean;

	/**
	 * Whether to retry on HTTP/network errors
	 * @default true
	 */
	shouldRetryOnError: boolean;

	/**
	 * Whether to exclude TaskCancellationError from retry logic
	 * User cancellations should not be retried
	 * @default true
	 */
	excludeCancellation: boolean;
}

/**
 * Default retry configuration for AI requests
 * - 1 retry attempt
 * - 1500ms delay between retries
 * - Validates responses and retries on errors
 * - Excludes user cancellations
 */
export const DEFAULT_AI_RETRY_CONFIG: RetryConfig = {
	maxRetries: 1,
	retryDelay: 1500,
	shouldRetryOnInvalidResponse: true,
	shouldRetryOnError: true,
	excludeCancellation: true,
};

/**
 * No retry configuration
 * - Disables all retries
 * - Use when retries are not desired
 */
export const NO_RETRY_CONFIG: RetryConfig = {
	maxRetries: 0,
	retryDelay: 0,
	shouldRetryOnInvalidResponse: false,
	shouldRetryOnError: false,
	excludeCancellation: true,
};

/**
 * Aggressive retry configuration
 * - 2 retry attempts
 * - 2000ms delay between retries
 * - Validates responses and retries on errors
 * - Use for critical operations that need higher reliability
 */
export const AGGRESSIVE_RETRY_CONFIG: RetryConfig = {
	maxRetries: 2,
	retryDelay: 2000,
	shouldRetryOnInvalidResponse: true,
	shouldRetryOnError: true,
	excludeCancellation: true,
};
